/**
 * Slack Integration API Service
 *
 * Service functions for interacting with the Slack integration API endpoints.
 * Follows the same pattern as jiraIntegrationService.ts.
 */

import { apiClient } from "./api";
import type {
  SlackIntegrationRead,
  SlackIntegrationCreate,
  SlackIntegrationUpdate,
  SlackConnectionTest,
  SlackWebhookTestRequest,
} from "@/types/slackIntegration";

const API_PREFIX = "/api/v1/integrations/slack";

/**
 * Get the current organization's Slack integration.
 * Returns the integration or throws (404 if not configured).
 */
export async function getSlackIntegration(): Promise<SlackIntegrationRead> {
  return apiClient.get<SlackIntegrationRead>(API_PREFIX);
}

/**
 * Create a new Slack integration for the current organization.
 */
export async function createSlackIntegration(
  data: SlackIntegrationCreate
): Promise<SlackIntegrationRead> {
  return apiClient.post<SlackIntegrationRead>(API_PREFIX, data);
}

/**
 * Update the current organization's Slack integration.
 */
export async function updateSlackIntegration(
  data: SlackIntegrationUpdate
): Promise<SlackIntegrationRead> {
  return apiClient.patch<SlackIntegrationRead>(API_PREFIX, data);
}

/**
 * Delete the current organization's Slack integration.
 */
export async function deleteSlackIntegration(): Promise<void> {
  return apiClient.delete(API_PREFIX);
}

/**
 * Test the Slack webhook connection.
 *
 * - With `credentials`: tests inline webhook URL (pre-save testing).
 * - Without `credentials`: tests stored webhook from the DB.
 */
export async function testSlackConnection(
  credentials?: SlackWebhookTestRequest
): Promise<SlackConnectionTest> {
  const url = `${API_PREFIX}/test`;
  return apiClient.post<SlackConnectionTest>(url, credentials);
}
